import { Command } from 'cmdk'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  LuAnchor,
  LuBookCopy,
  LuChartLine,
  LuFileText,
  LuHouse,
  LuListChecks,
  LuMap,
  LuPackage,
  LuPlus,
  LuReceipt,
  LuSearch,
  LuSettings,
  LuShip,
  LuSignature,
  LuUsers,
} from 'react-icons/lu'
import { useCommandSearch } from '../../hooks/useCommandSearch'
import classes from './CommandPalette.module.css'

interface Props {
  opened: boolean
  onClose: () => void
  onNavigate: (href: string) => void
}

type StaticItem = {
  id: string
  label: string
  /** Extra keywords (slug, alias) used by cmdk's fuzzy matcher. */
  keywords: string[]
  href: string
  icon: React.ComponentType<{ size?: number }>
}

/**
 * Linear-style command palette. Cmd/Ctrl+K trigger lives in MarinaLayout;
 * this component owns the dialog UI, fuzzy matching (cmdk), and result
 * categories.
 *
 * Categories:
 *  - Quick actions: create new <entity>
 *  - Go to: every dashboard page
 *  - Entity search: customers / boats / bookings / invoices / quotes
 *    (live results from useCommandSearch)
 */
export function CommandPalette({ opened, onClose, onNavigate }: Props) {
  const { t } = useTranslation()
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const { data: hits } = useCommandSearch(debounced)

  // Debounce 140ms so we don't fire on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(term), 140)
    return () => clearTimeout(handle)
  }, [term])

  // Reset input when reopening so palette feels fresh
  useEffect(() => {
    if (!opened) {
      setTerm('')
      setDebounced('')
    }
  }, [opened])

  if (!opened) return null

  const actions: StaticItem[] = [
    {
      id: 'new-booking',
      label: t('palette.action_new_booking'),
      keywords: ['booking', 'reservation', 'tambat'],
      href: '/dashboard/bookings/new',
      icon: LuPlus,
    },
    {
      id: 'new-customer',
      label: t('palette.action_new_customer'),
      keywords: ['customer', 'client', 'pelanggan'],
      href: '/dashboard/accounts/customers/new',
      icon: LuPlus,
    },
    {
      id: 'new-boat',
      label: t('palette.action_new_boat'),
      keywords: ['boat', 'vessel', 'yacht', 'kapal'],
      href: '/dashboard/accounts/boats/new',
      icon: LuPlus,
    },
    {
      id: 'new-quote',
      label: t('palette.action_new_quote'),
      keywords: ['quote', 'quotation', 'penawaran'],
      href: '/dashboard/billing/quotes/new',
      icon: LuPlus,
    },
    {
      id: 'new-invoice',
      label: t('palette.action_new_invoice'),
      keywords: ['invoice', 'bill', 'tagihan', 'faktur'],
      href: '/dashboard/billing/invoices/new',
      icon: LuPlus,
    },
    {
      id: 'new-contract',
      label: t('palette.action_new_contract'),
      keywords: ['contract', 'agreement', 'kontrak'],
      href: '/dashboard/accounts/contracts/new',
      icon: LuPlus,
    },
    {
      id: 'new-assignment',
      label: t('palette.action_new_assignment'),
      keywords: ['task', 'assignment', 'tugas'],
      href: '/dashboard/facility/assignments/new',
      icon: LuPlus,
    },
    {
      id: 'new-inventory',
      label: t('palette.action_new_inventory'),
      keywords: ['inventory', 'stock', 'inventaris'],
      href: '/dashboard/facility/inventory/new',
      icon: LuPlus,
    },
  ]

  const pages: StaticItem[] = [
    {
      id: 'p-overview',
      label: t('page.overview.title', { defaultValue: 'Overview' }),
      keywords: ['dashboard', 'home', 'ikhtisar', 'beranda'],
      href: '/dashboard',
      icon: LuHouse,
    },
    {
      id: 'p-analytics',
      label: t('nav.analytics', { defaultValue: 'Analytics' }),
      keywords: ['analytics', 'reports', 'analitik', 'laporan'],
      href: '/dashboard/analytics',
      icon: LuChartLine,
    },
    {
      id: 'p-map',
      label: t('nav.map', { defaultValue: 'Berth map' }),
      keywords: ['berth map', 'occupancy', 'dermaga', 'peta'],
      href: '/dashboard/occupancy/map',
      icon: LuMap,
    },
    {
      id: 'p-schedule',
      label: t('nav.schedule', { defaultValue: 'Schedule' }),
      keywords: ['calendar', 'schedule', 'jadwal'],
      href: '/dashboard/occupancy/calendar',
      icon: LuMap,
    },
    {
      id: 'p-arrivals',
      label: t('nav.arrivals', { defaultValue: 'Arrivals & Departures' }),
      keywords: ['arrivals', 'departures', 'check-in', 'tiba'],
      href: '/dashboard/occupancy/arrivals',
      icon: LuAnchor,
    },
    {
      id: 'p-requests',
      label: t('nav.requests', { defaultValue: 'Booking requests' }),
      keywords: ['requests', 'pending', 'permohonan'],
      href: '/dashboard/reservations/requests',
      icon: LuAnchor,
    },
    {
      id: 'p-bookings',
      label: t('nav.bookings', { defaultValue: 'Bookings' }),
      keywords: ['bookings', 'reservations', 'booking'],
      href: '/dashboard/reservations/bookings',
      icon: LuAnchor,
    },
    {
      id: 'p-customers',
      label: t('nav.customers', { defaultValue: 'Customers' }),
      keywords: ['customers', 'clients', 'pelanggan'],
      href: '/dashboard/accounts/customers',
      icon: LuUsers,
    },
    {
      id: 'p-boats',
      label: t('nav.boats', { defaultValue: 'Boats' }),
      keywords: ['boats', 'vessels', 'kapal'],
      href: '/dashboard/accounts/boats',
      icon: LuShip,
    },
    {
      id: 'p-contracts',
      label: t('nav.contracts', { defaultValue: 'Contracts' }),
      keywords: ['contracts', 'agreements', 'kontrak'],
      href: '/dashboard/accounts/contracts',
      icon: LuSignature,
    },
    {
      id: 'p-quotes',
      label: t('nav.quotes', { defaultValue: 'Quotes' }),
      keywords: ['quotes', 'quotations', 'penawaran'],
      href: '/dashboard/billing/quotes',
      icon: LuFileText,
    },
    {
      id: 'p-invoices',
      label: t('nav.invoices', { defaultValue: 'Invoices' }),
      keywords: ['invoices', 'billing', 'tagihan', 'faktur'],
      href: '/dashboard/billing/invoices',
      icon: LuBookCopy,
    },
    {
      id: 'p-tasks',
      label: t('nav.tasks', { defaultValue: 'Tasks' }),
      keywords: ['tasks', 'assignments', 'tugas'],
      href: '/dashboard/facility/assignments',
      icon: LuListChecks,
    },
    {
      id: 'p-inventory',
      label: t('nav.inventory', { defaultValue: 'Inventory' }),
      keywords: ['inventory', 'stock', 'inventaris'],
      href: '/dashboard/facility/inventory',
      icon: LuPackage,
    },
    {
      id: 'p-settings',
      label: t('nav.settings', { defaultValue: 'Settings' }),
      keywords: ['settings', 'configuration', 'pengaturan'],
      href: '/dashboard/settings',
      icon: LuSettings,
    },
  ]

  const select = (href: string) => {
    onNavigate(href)
    onClose()
  }

  const dialog = (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click-to-dismiss; Esc + dialog focus trap handle keyboard
    <div
      className={classes.overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <Command
        label={t('palette.trigger')}
        className={classes.dialog}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
          }
        }}
      >
        <div className={classes.inputWrap}>
          <LuSearch size={16} className={classes.searchIcon} />
          <Command.Input
            value={term}
            onValueChange={setTerm}
            placeholder={t('palette.placeholder')}
            className={classes.input}
            autoFocus
          />
          <span className={classes.escBadge}>Esc</span>
        </div>

        <Command.List className={classes.list}>
          <Command.Empty>
            <div className={classes.empty}>
              <div className={classes.emptyTitle}>{t('palette.empty')}</div>
              <div className={classes.emptyHint}>{t('palette.empty_hint')}</div>
            </div>
          </Command.Empty>

          <Command.Group heading={t('palette.group_actions')} className={classes.group}>
            {actions.map((a) => (
              <PaletteItem key={a.id} item={a} onSelect={select} icon={<LuPlus size={14} />} />
            ))}
          </Command.Group>

          <Command.Group heading={t('palette.group_pages')} className={classes.group}>
            {pages.map((p) => {
              const Icon = p.icon
              return <PaletteItem key={p.id} item={p} onSelect={select} icon={<Icon size={14} />} />
            })}
          </Command.Group>

          {hits && hits.customers.length > 0 && (
            <Command.Group heading={t('palette.group_customers')} className={classes.group}>
              {hits.customers.map((c) => (
                <DynamicItem key={c.id} hit={c} onSelect={select} icon={<LuUsers size={14} />} />
              ))}
            </Command.Group>
          )}
          {hits && hits.boats.length > 0 && (
            <Command.Group heading={t('palette.group_boats')} className={classes.group}>
              {hits.boats.map((b) => (
                <DynamicItem key={b.id} hit={b} onSelect={select} icon={<LuShip size={14} />} />
              ))}
            </Command.Group>
          )}
          {hits && hits.bookings.length > 0 && (
            <Command.Group heading={t('palette.group_bookings')} className={classes.group}>
              {hits.bookings.map((b) => (
                <DynamicItem key={b.id} hit={b} onSelect={select} icon={<LuAnchor size={14} />} />
              ))}
            </Command.Group>
          )}
          {hits && hits.invoices.length > 0 && (
            <Command.Group heading={t('palette.group_invoices')} className={classes.group}>
              {hits.invoices.map((i) => (
                <DynamicItem key={i.id} hit={i} onSelect={select} icon={<LuReceipt size={14} />} />
              ))}
            </Command.Group>
          )}
          {hits && hits.quotes.length > 0 && (
            <Command.Group heading={t('palette.group_quotes')} className={classes.group}>
              {hits.quotes.map((q) => (
                <DynamicItem key={q.id} hit={q} onSelect={select} icon={<LuFileText size={14} />} />
              ))}
            </Command.Group>
          )}
        </Command.List>

        <div className={classes.footer}>
          <span>
            <kbd className={classes.kbd}>↑</kbd>
            <kbd className={classes.kbd}>↓</kbd>
            {t('palette.footer_navigate')}
          </span>
          <span>
            <kbd className={classes.kbd}>↵</kbd>
            {t('palette.footer_select')}
          </span>
          <span className={classes.footerSpacer} />
          <span>
            <kbd className={classes.kbd}>Esc</kbd>
            {t('palette.footer_close')}
          </span>
        </div>
      </Command>
    </div>
  )

  return createPortal(dialog, document.body)
}

function PaletteItem({
  item,
  onSelect,
  icon,
}: {
  item: StaticItem
  onSelect: (href: string) => void
  icon: React.ReactNode
}) {
  return (
    <Command.Item
      value={`${item.label} ${item.keywords.join(' ')}`}
      onSelect={() => onSelect(item.href)}
      className={classes.item}
    >
      <span className={classes.itemIcon}>{icon}</span>
      <div className={classes.itemLabel}>
        <div className={classes.itemTitle}>{item.label}</div>
      </div>
    </Command.Item>
  )
}

function DynamicItem({
  hit,
  onSelect,
  icon,
}: {
  hit: { id: string; label: string; sublabel?: string; href: string }
  onSelect: (href: string) => void
  icon: React.ReactNode
}) {
  return (
    <Command.Item
      value={`${hit.label} ${hit.sublabel ?? ''}`}
      onSelect={() => onSelect(hit.href)}
      className={classes.item}
    >
      <span className={classes.itemIcon}>{icon}</span>
      <div className={classes.itemLabel}>
        <div className={classes.itemTitle}>{hit.label}</div>
        {hit.sublabel && <div className={classes.itemSub}>{hit.sublabel}</div>}
      </div>
    </Command.Item>
  )
}
